import React, { useState, useEffect } from "react";
import { axiosPrivate } from "../../../api/axios";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, TextField, Paper, IconButton, Collapse, Alert } from "@mui/material";
import Grid from "@mui/material/Grid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const NewProjectDialog = ({ open, onClose, onProjectAdded }) => {
  const [formValues, setFormValues] = useState({ project_name: "", project_code: "" });
  const [errors, setErrors] = useState({ project_name: "", project_code: "" });
  const [loading, setLoading] = useState(false);
  const [openInfo, setOpenInfo] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      setFormValues({ project_name: "", project_code: "" });
      setErrors({ project_name: "", project_code: "" });
      setOpenInfo(true);
      setLoading(false);
      setErrorMsg("");
    }
  }, [open]);

  const validateField = (field, value) => {
    let errorMessage = "";
    if (field === "project_code") {
      const regex = /^[a-zA-Z0-9]{2,6}$/;
      if (!regex.test(value)) errorMessage = "공백없이 영문 알파벳 및 숫자 2~6 글자로 입력해주세요.";
    }
    if (field === "project_name" && value.trim() === "") {
      errorMessage = "프로젝트 이름을 입력해주세요.";
    }
    setErrors((prev) => ({ ...prev, [field]: errorMessage }));
    return errorMessage === "";
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormValues((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    const { project_name, project_code } = formValues;
    const isNameValid = validateField("project_name", project_name);
    const isCodeValid = validateField("project_code", project_code);
    if (!isNameValid || !isCodeValid) return;
    try {
      setLoading(true);
      await axiosPrivate.post("/project/newProject", { project_name, project_code });
      try { onProjectAdded && onProjectAdded(); } catch (_) {}
      onClose && onClose();
    } catch (err) {
      const status = err?.response?.status;
      const serverMessage = err?.response?.data?.message || "";
      if (status === 409) {
        const msg = String(serverMessage || "");
        // 코드 중복인지, 다른 중복(폴더 등)인지 구분 표시
        if (/코드/i.test(msg) || /code/i.test(msg)) {
          setErrors((prev) => ({ ...prev, project_code: "이미 존재하는 프로젝트 코드입니다." }));
        } else {
          setErrorMsg(msg || "중복으로 인해 생성할 수 없습니다.");
        }
      } else if (status === 403) {
        setErrorMsg(serverMessage || "권한이 없습니다.");
      } else if (status === 400) {
        setErrorMsg(serverMessage || "요청이 유효하지 않습니다.");
      } else {
        console.error(err);
        setErrorMsg(serverMessage || "프로젝트 생성 오류");
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formValues.project_name.trim().length > 0 && /^[a-zA-Z0-9]{2,6}$/.test(formValues.project_code) && !errors.project_name && !errors.project_code;

  return (
    <Dialog fullWidth open={open} maxWidth="sm" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>신규 프로젝트</DialogTitle>
        <DialogContent>
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: openInfo ? 1 : 0 }}>
                <Typography variant="subtitle1">프로젝트 정보</Typography>
                <IconButton size="small" onClick={() => setOpenInfo(!openInfo)} aria-label="toggle-info">
                  {openInfo ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>
              <Collapse in={openInfo} unmountOnExit>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      name="project_name"
                      value={formValues.project_name}
                      autoComplete="off"
                      label="프로젝트 이름"
                      onChange={handleChange("project_name")}
                      placeholder="프로젝트 이름"
                      required
                      error={Boolean(errors.project_name)}
                      helperText={errors.project_name || ""}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      name="project_code"
                      value={formValues.project_code}
                      autoComplete="off"
                      label="프로젝트 식별 코드"
                      placeholder="프로젝트 식별 코드"
                      helperText={errors.project_code || "공백없이 영문 알파벳 및 숫자 2~6 글자"}
                      onChange={handleChange("project_code")}
                      required
                      error={Boolean(errors.project_code)}
                    />
                  </Grid>
                </Grid>
              </Collapse>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button variant="contained" sx={{ mr: 1 }} type="submit" disabled={!isFormValid || loading}>
            생성
          </Button>
          <Button variant="tonal" color="secondary" onClick={onClose} disabled={loading}>
            취소
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NewProjectDialog;